import { redo, undo } from 'prosemirror-history'
import { MarkType } from 'prosemirror-model'
import { Command, EditorState, TextSelection } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'

import { util as codexUtil, MarkTypeMap } from '@showrunner/codex'
import { schema } from '@showrunner/prose-schemas'

import { EditorManager } from '#choo-app/lib/editor/EditorManager'
import { blockInfoPluginKey } from '#choo-app/lib/editor/plugins/block-info'
import { commentsPluginKey } from '#choo-app/lib/editor/plugins/comments'
import { getConfigData } from '#choo-app/lib/editor/plugins/configData'
import {
  docInfoPluginKey,
  DocInfoState,
} from '#choo-app/lib/editor/plugins/doc-info'
import { elementPickerKeys } from '#choo-app/lib/editor/plugins/element-picker-keymap'
import { getPaginationBreakdown } from '#choo-app/lib/editor/plugins/inlinePageBreaks/inlinePageBreaksPlugin'
import {
  getLockedPageInventory,
  getPagination,
} from '#choo-app/lib/editor/plugins/pagination/paginationPlugin'
import {
  getSelectionBlock,
  getSelectionBlockType,
  isSingleBlockTextSelection,
  selectionContainsMark,
  shouldDisableAlignment,
} from '#util'
import { isNavLinkElement, NavLinkData } from '#util/constants'

import { BaseModel } from './BaseModel'

export const PmEditor = BaseModel.named('PmEditor')
  .props({
    editingHyperlink: false,
    // we track when users lose permission to edit the script during a session
    // to avoid prompting about unsaved edits from useAppUnloadHandler
    editabilityLost: false,
  })
  .volatile<{
    editorManager: EditorManager | null
    editorState: EditorState | null
  }>(() => ({
    editorManager: null,
    // we could get the editorState via the editorManager, but instead we
    // store it separately and re-set it any time the editorState changes so
    // it becomes observable
    editorState: null,
  }))
  .views((self) => ({
    withEditorState<T>(fn: (state: EditorState) => T): T | undefined {
      if (!self.editorState) return undefined
      return fn(self.editorState)
    },
    get editorView(): EditorView | null {
      return self.editorManager?.view ?? null
    },
    get configData() {
      return self.editorState ? getConfigData(self.editorState) : null
    },
    get paginationBreakdown() {
      return self.editorState ? getPaginationBreakdown(self.editorState) : null
    },
    get pagination() {
      return self.editorState ? getPagination(self.editorState) : null
    },
    get pageLocking() {
      return self.editorState ? getLockedPageInventory(self.editorState) : null
    },
    get elementPickerKeys() {
      return this.withEditorState(elementPickerKeys) ?? []
    },
  }))
  .views((self) => ({
    get docInfo(): DocInfoState | null {
      if (self.editorState) {
        return docInfoPluginKey.getState(self.editorState) ?? null
      }
      return null
    },
    get navLinks(): NavLinkData[] {
      return self.editorState
        ? ((blockInfoPluginKey
            .getState(self.editorState)
            ?.filter((b) => isNavLinkElement(b.type)) as NavLinkData[]) ?? [])
        : []
    },
    get commentInventory() {
      if (self.editorState) {
        return commentsPluginKey.getState(self.editorState)
      }
    },
    get selection() {
      return self.editorState?.selection
    },
    get currentBlockStartPos(): number | null {
      return this.selection?.$from.start(2) ?? null
    },
    getSelectionTiming(readRate: number) {
      if (this.selection && this.selection.content().size > 0) {
        return new codexUtil.FragmentBreakdown({
          fragment: this.selection.content().content,
          readRate,
        }).timing
      }
    },
    get docTiming() {
      return this.docInfo?.timing
    },
    get textSelection(): TextSelection | undefined {
      return self.editorState?.selection instanceof TextSelection
        ? self.editorState.selection
        : undefined
    },
    get selectionTouchesEndOfBlock(): boolean {
      if (!this.selection) return false
      const { $from, $to } = this.selection
      if (!$from.parent.isBlock) return false
      if ($to.parentOffset === $to.parent.content.size) return true
      return false
    },
    get singleBlockTextSelection(): boolean {
      if (self.editorState === null) return false
      return isSingleBlockTextSelection(self.editorState)
    },
    get selectionBlockType() {
      if (self.editorState === null) return
      if (!this.singleBlockTextSelection) return
      return getSelectionBlockType(self.editorState)
    },
    /**
     * attempt to pluck the relevant attribute from
     * plain cursors and text selections spanning a single block
     * otherwise return null
     *
     * @returns {string|undefined} - 'right' | 'center' | 'left' | undefined
     */
    get selectionAlignment(): string | undefined {
      if (self.editorState === null) return
      if (!this.singleBlockTextSelection) return
      return getSelectionBlock(self.editorState)?.attrs?.alignment
    },
    get disableAlignment(): boolean {
      if (self.editorState === null) return true
      return shouldDisableAlignment(self.editorState)
    },
    get canUndo(): boolean {
      if (!self.editorState) return false
      return undo(self.editorState)
    },
    get canRedo(): boolean {
      if (!self.editorState) return false
      return redo(self.editorState)
    },
    get selectionColor(): string {
      if (!self.editorState) return ''
      let color = ''
      self.editorState.doc.nodesBetween(
        self.editorState.selection.from,
        self.editorState.selection.to,
        (node) => {
          node.marks.forEach((mark) => {
            if (mark.type.name === MarkTypeMap.COLOR && !color) {
              color = mark.attrs.color
            }
          })
        },
      )
      return color
    },
    get selectionBgColor(): string {
      if (!self.editorState) return ''
      let bgColor = ''
      self.editorState.doc.nodesBetween(
        self.editorState.selection.from,
        self.editorState.selection.to,
        (node) => {
          node.marks.forEach((mark) => {
            if (mark.type.name === MarkTypeMap.BACKGROUND_COLOR && !bgColor) {
              bgColor = mark.attrs.color
            }
          })
        },
      )
      return bgColor
    },
  }))
  .views((self) => ({
    get currentPageIndex(): number | null {
      if ((!self.paginationBreakdown && !self.pagination) || !self.selection) {
        return null
      }

      if (self.paginationBreakdown) {
        return self.paginationBreakdown.pageIndexAtPosition(self.selection.from)
      }

      if (self.pagination) {
        return self.pagination.pageAt(self.selection.from)?.index ?? null
      }

      return null
    },
    selectionContainsMark(type: MarkType): boolean {
      const { editorState } = self
      if (!editorState) return false
      return selectionContainsMark(type, editorState)
    },
    get canAddComment(): boolean {
      return !!self.selection && !self.selection.empty
    },
    get hasUnsavedComment(): boolean {
      return !!self.commentInventory?.unsavedComment
    },
    get canFormatText(): boolean {
      return self.editorState && self.editorView
        ? self.editorView.editable && !!self.selection
        : false
    },
    get hasModifiableSelectedText(): boolean {
      if (self.editorState && self.editorView) {
        return this.canFormatText && !self.selection?.empty
      }
      return false
    },
    get hyperlinkingEnabled(): boolean {
      if (!this.hasModifiableSelectedText) {
        return false
      }
      if (!self.singleBlockTextSelection) {
        return false
      }
      // check there's no overlapping hyperlink
      return !this.selectionContainsMark(schema.marks.link)
    },
  }))
  .actions((self) => ({
    setEditingHyperlink(value: boolean) {
      self.editingHyperlink = value
    },
    setEditorManager(value: EditorManager | null) {
      self.editorManager = value
      self.editorState = value?.view?.state ?? null
    },
    syncEditorView() {
      const state = self.editorManager?.view?.state ?? null
      self.editorState = state
    },
    focusEditor() {
      if (self.editorView) {
        self.editorView.focus()
        this.syncEditorView()
      }
    },
    rerender() {
      if (self.editorView) {
        const { tr } = self.editorView.state
        tr.setMeta('choo', true)
        self.editorView.dispatch(tr)
      }
    },
    selectPosition(pos: number) {
      if (self.editorView) {
        try {
          // this throws if pos is out of range
          const position = self.editorView.state.doc.resolve(pos)
          const selection = TextSelection.findFrom(position, 1, true)
          if (selection) {
            self.editorView.dispatch(
              self.editorView.state.tr.setSelection(selection),
            )
            this.focusEditor()
          }
        } catch {
          // noop
        }
      }
    },
    setEditabilityLost(val: boolean) {
      self.editabilityLost = val
    },
    /**
     * Run a [command](https://prosemirror.net/docs/ref/#state.Command) for the
     * current instance of the editor
     * @param command
     * @returns boolean - true if executed, false if not
     */
    invoke(command: Command): boolean {
      if (!self.editorView) return false
      const { state, dispatch } = self.editorView
      return command(state, dispatch, self.editorView)
    },
  }))
